import { useEffect, useState, useRef } from "react";
import { useHub } from "../../context/HubContext";
import { getHubById, updateHub } from "../../services/hubService";
import { getHubPartners } from "../../services/hubPartnerService";
import { formatDate } from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import EditableField from "../../components/ingroove/hub/EditableField";
import BankingDetailsForm, {
  BankingDetailsView,
} from "../../components/ingroove/hub/BankingDetailsForm";
import {
  Building2,
  MapPin,
  Pencil,
  X,
  Save,
  Camera,
  Handshake,
  CreditCard,
} from "lucide-react";

const roleBadgeVariant = (role) => {
  switch (role?.toLowerCase()) {
    case "admin":
      return "danger";
    case "operations":
      return "info";
    case "finance":
      return "warning";
    case "content":
      return "success";
    default:
      return "default";
  }
};

const HubDetail = () => {
  const { hub, setHub } = useHub();
  const fileInputRef = useRef(null);

  const [data, setData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [showAccount, setShowAccount] = useState(false);
  const [fields, setFields] = useState({});

  useEffect(() => {
    if (!hub?.id) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const [hubRes, partnersRes] = await Promise.all([
          getHubById(hub.id),
          getHubPartners(hub.id),
        ]);
        setData(hubRes);
        setPartners(partnersRes);
        initFields(hubRes);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [hub?.id]);

  const initFields = (h) => {
    setFields({
      name: h.name ?? "",
      bio: h.bio ?? "",
      showAddress: h.showAddress ?? false,
      addressId: h.address?.id ?? "",
      address_DisplayName: h.address?.displayName ?? "",
      address_FullAddress: h.address?.fullAddress ?? "",
      address_City: h.address?.city ?? "",
      address_Province: h.address?.province ?? "",
      address_PostalCode: h.address?.postalCode ?? "",
      address_Latitude: h.address?.latitude ?? 0,
      address_Longitude: h.address?.longitude ?? 0,
      bankAccount_Id: h.bankingDetail?.id ?? "",
      bankAccount_Name: h.bankingDetail?.name ?? "",
      bankAccount_BankName: h.bankingDetail?.bankName ?? "",
      bankAccount_AccountNumber: h.bankingDetail?.accountNumber ?? "",
      bankAccount_BranchCode: h.bankingDetail?.branchCode ?? "",
      userId: h.userId,
    });
  };

  const setField = (key) => (val) => setFields((f) => ({ ...f, [key]: val }));

  const handleBankingChange = (key, value) =>
    setFields((f) => ({ ...f, [key]: value }));

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCancel = () => {
    setEditMode(false);
    setImagePreview(null);
    setImageFile(null);
    setError("");
    if (data) initFields(data);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("Id", hub.id);
      formData.append("Name", fields.name);
      formData.append("Bio", fields.bio);
      formData.append("UserId", fields.userId);
      formData.append("ShowAddress", fields.showAddress);
      formData.append("AddressId", fields.addressId);
      formData.append("Address_DisplayName", fields.address_DisplayName);
      formData.append("Address_FullAddress", fields.address_FullAddress);
      formData.append("Address_City", fields.address_City);
      formData.append("Address_Province", fields.address_Province);
      formData.append("Address_PostalCode", fields.address_PostalCode);
      formData.append("Address_Latitude", fields.address_Latitude);
      formData.append("Address_Longitude", fields.address_Longitude);
      formData.append("BankAccount_Id", fields.bankAccount_Id);
      formData.append("BankAccount_HubId", hub.id);
      formData.append(
        "BankAccount_Name",
        fields.bankAccount_Name || fields.name,
      );
      formData.append("BankAccount_BankName", fields.bankAccount_BankName);
      formData.append(
        "BankAccount_AccountNumber",
        fields.bankAccount_AccountNumber,
      );
      formData.append("BankAccount_BranchCode", fields.bankAccount_BranchCode);
      if (imageFile) formData.append("file", imageFile);

      const res = await updateHub(hub.id, formData);
      if (res.isSuccess) {
        const updated = await getHubById(hub.id);
        setData(updated);
        initFields(updated);
        setHub((prev) => ({
          ...prev,
          name: updated.name,
          image: updated.image,
        }));
        setEditMode(false);
        setImagePreview(null);
        setImageFile(null);
      } else {
        setError(res.message ?? "Failed to save changes.");
      }
    } catch (err) {
      setError(err.response?.data?.message ?? "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-48 bg-bg-surface rounded-2xl" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-64 bg-bg-surface rounded-2xl" />
            <div className="h-48 bg-bg-surface rounded-2xl" />
            <div className="h-48 bg-bg-surface rounded-2xl" />
          </div>
          <div className="h-64 bg-bg-surface rounded-2xl" />
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Building2 size={32} className="text-border-hover mb-3" />
        <p className="text-sm text-text-muted">Hub not found.</p>
      </div>
    );

  const displayImage = imagePreview ?? data.image;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hub Details"
        subtitle={data.name}
        action={
          !editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="flex items-center gap-2 px-4 py-2 bg-bg-surface border border-border text-text-secondary text-xs font-semibold rounded-xl hover:border-primary hover:text-primary transition"
            >
              <Pencil size={13} /> Edit Hub
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 bg-bg-surface border border-border text-text-muted text-xs font-semibold rounded-xl hover:border-border-hover transition"
              >
                <X size={13} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 ingroove-gradient-bg text-white text-xs font-semibold rounded-xl hover:opacity-90 transition disabled:opacity-50 shadow-glow"
              >
                <Save size={13} />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )
        }
      />

      {/* Hero banner */}
      <div className="relative h-48 bg-bg-elevated rounded-2xl overflow-hidden">
        {displayImage ? (
          <img
            src={displayImage}
            alt={data.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Building2 size={40} className="text-border-hover" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-4 left-5">
          <h2 className="text-xl font-bold text-white">{data.name}</h2>
          {data.address?.displayName && (
            <div className="flex items-center gap-1.5 text-white/60 text-xs mt-0.5">
              <MapPin size={11} />
              {data.address.displayName}
            </div>
          )}
        </div>
        {editMode && (
          <>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-sm text-white text-xs rounded-xl border border-white/20 hover:bg-black/70 transition"
            >
              <Camera size={13} /> Change Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
          </>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          {error}
        </p>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — profile + address + banking */}
        <div className="lg:col-span-2 space-y-5">
          {/* Hub Profile */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <Building2 size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Hub Profile
              </h3>
            </div>
            <div className="space-y-4">
              <EditableField
                label="Hub Name"
                value={fields.name}
                onChange={setField("name")}
                editMode={editMode}
                placeholder="Your hub name"
              />
              <EditableField
                label="Bio"
                value={fields.bio}
                onChange={setField("bio")}
                editMode={editMode}
                placeholder="Tell people about your hub..."
                textarea
              />
              {editMode && (
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="showAddress"
                    checked={fields.showAddress ?? false}
                    onChange={(e) => setField("showAddress")(e.target.checked)}
                    className="accent-primary"
                  />
                  <label
                    htmlFor="showAddress"
                    className="text-xs text-text-secondary"
                  >
                    Show address publicly on hub profile
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <MapPin size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Address
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <EditableField
                  label="Display Name"
                  value={fields.address_DisplayName}
                  onChange={setField("address_DisplayName")}
                  editMode={editMode}
                  placeholder="e.g. Soccer City, Johannesburg"
                />
              </div>
              <div className="sm:col-span-2">
                <EditableField
                  label="Full Address"
                  value={fields.address_FullAddress}
                  onChange={setField("address_FullAddress")}
                  editMode={editMode}
                  placeholder="Full street address"
                />
              </div>
              <EditableField
                label="City"
                value={fields.address_City}
                onChange={setField("address_City")}
                editMode={editMode}
                placeholder="City"
              />
              <EditableField
                label="Province"
                value={fields.address_Province}
                onChange={setField("address_Province")}
                editMode={editMode}
                placeholder="Province"
              />
              <EditableField
                label="Postal Code"
                value={fields.address_PostalCode}
                onChange={setField("address_PostalCode")}
                editMode={editMode}
                placeholder="0000"
              />
            </div>
          </div>

          {/* Banking Details — uses shared component */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-5">
              <CreditCard size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Banking Details
              </h3>
            </div>

            {editMode ? (
              <BankingDetailsForm
                fields={fields}
                onChange={handleBankingChange}
              />
            ) : (
              <BankingDetailsView
                bankingDetail={data.bankingDetail}
                maskedBankAccount={data.maskedBankAccount}
                showAccount={showAccount}
                onToggleAccount={() => setShowAccount((p) => !p)}
              />
            )}
          </div>
        </div>

        {/* Right — partners + hub meta */}
        <div className="space-y-5">
          {/* Partners */}
          <div className="bg-bg-surface border border-border rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
              <Handshake size={15} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">
                Hub Partners
              </h3>
              <span className="ml-auto text-xs text-text-muted">
                {partners.length} total
              </span>
            </div>
            {partners.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <p className="text-xs text-text-muted">No partners yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {partners.slice(0, 5).map((partner) => (
                  <div
                    key={partner.id}
                    className="flex items-center gap-3 px-5 py-3"
                  >
                    <Avatar
                      name={partner.user?.name}
                      surname={partner.user?.surname}
                      image={partner.user?.image}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-text-primary truncate">
                        {partner.user?.name} {partner.user?.surname}
                      </p>
                      <p className="text-xs text-text-muted truncate">
                        {partner.user?.email}
                      </p>
                    </div>
                    <Badge
                      label={partner.role?.name ?? "—"}
                      variant={roleBadgeVariant(partner.role?.name)}
                    />
                  </div>
                ))}
                {partners.length > 5 && (
                  <div className="px-5 py-2.5 text-xs text-text-muted">
                    +{partners.length - 5} more partners
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hub meta */}
          <div className="bg-bg-surface border border-border rounded-2xl p-5">
            <h3 className="text-xs text-text-muted uppercase tracking-wide mb-4">
              Hub Info
            </h3>
            <div className="space-y-3 text-xs">
              <div className="flex justify-between">
                <span className="text-text-muted">Hub ID</span>
                <code className="text-text-hint">
                  {data.id?.slice(0, 8)}...
                </code>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Created</span>
                <span className="text-text-secondary">
                  {formatDate(data.createdDate)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Address visible</span>
                <span
                  className={
                    data.showAddress ? "text-primary" : "text-text-muted"
                  }
                >
                  {data.showAddress ? "Yes" : "No"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Bank</span>
                <span className="text-text-secondary">
                  {data.bankingDetail?.bankName || "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Account</span>
                <span className="text-text-secondary">
                  {data.maskedBankAccount || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HubDetail;
